// DAEMON's voice — the part that decides *what* to say and *whether now
// is a good time*. It says nothing itself: no DOM, no SpeechSynthesis,
// no timers, no `Date`. It takes a tick and some events and returns at
// most one line. The shell renders it and, if TTS is on, speaks it.
//
// Three problems this file exists to solve, in order of how much damage
// they were doing:
//
//   1. **Repetition.** The old system used a uniform random pick, which
//      means a 20-line pool repeats within the first handful of draws
//      (birthday problem: ~50% chance of a repeat by draw 6). Repetition
//      is the specific failure mode that killed Clippy — the Stanford
//      study of it flagged "always told the same jokes at the same
//      times" as its own unanswered question. So: draw *without
//      replacement* from a shuffled bag, reshuffle only on exhaustion,
//      and never let a reshuffle's first line repeat the previous draw.
//
//   2. **Pacing.** The old system spoke on every poke and every speech
//      call began with `synth.cancel()`, so at any real cadence the
//      player heard the first syllable of every line and finished none.
//      Grazing thirty bullets a second must not queue thirty lines. So:
//      a global rate limit, a per-pool cooldown, and an utterance that
//      is allowed to *finish* unless something more important happens.
//
//   3. **Talking over things the player must read.** Chatter is
//      suppressed on the draft and results screens.
//
// And because the whole sim is seeded and replayable, the voice is too:
// it draws from an `Rng`, never `Math.random`, so a replay says the
// same things at the same moments.

import type { Rng } from "./rng";
import type { PhaseId, RunStatus, SignalId, SimEvent } from "./types";

// ── Priority ──────────────────────────────────────────────────────────

/**
 * How much a line is allowed to elbow. `chatter` is colour and may be
 * dropped freely; `beat` is a structural moment (a card opening, a
 * signal landing); `critical` is something the player must not miss.
 *
 * The rule in one sentence: **critical may interrupt chatter, chatter
 * may never interrupt anything.**
 */
export type Priority = "chatter" | "beat" | "critical";

const RANK: Record<Priority, number> = { chatter: 0, beat: 1, critical: 2 };

// ── Pools ─────────────────────────────────────────────────────────────

/** Every named line pool. The script must supply all of them. */
export type PoolId =
  // Opening — the first ten seconds, which are the whole tutorial.
  | "intro"
  | "teachDodge"
  | "teachGraze"
  | "teachSignals"
  // Moment to moment.
  | "graze"
  | "grazeHot"
  | "playerHit"
  | "nearDeath"
  | "playerDown"
  // Spell cards.
  | "cardStart"
  | "cardClear"
  // Phases and their devices.
  | "phaseRl5"
  | "phaseRl4"
  | "phaseRl3"
  | "phaseRl2"
  | "phaseRl1"
  | "phaseClear"
  | "fork"
  | "zombie"
  | "shrink"
  // Signals — the player's verbs.
  | "sigterm"
  | "sigstop"
  | "sigkill"
  | "renice"
  | "strace"
  | "signalFail"
  // Between and after.
  | "draft"
  | "won"
  | "lost"
  | "restart"
  // Speedrunning.
  | "personalBest"
  | "ghostBeaten"
  | "ghostBehind";

export const POOL_IDS: readonly PoolId[] = [
  "intro",
  "teachDodge",
  "teachGraze",
  "teachSignals",
  "graze",
  "grazeHot",
  "playerHit",
  "nearDeath",
  "playerDown",
  "cardStart",
  "cardClear",
  "phaseRl5",
  "phaseRl4",
  "phaseRl3",
  "phaseRl2",
  "phaseRl1",
  "phaseClear",
  "fork",
  "zombie",
  "shrink",
  "sigterm",
  "sigstop",
  "sigkill",
  "renice",
  "strace",
  "signalFail",
  "draft",
  "won",
  "lost",
  "restart",
  "personalBest",
  "ghostBeaten",
  "ghostBehind",
];

export type PoolSpec = {
  priority: Priority;
  /** Ticks before this *category* may speak again. The cooldown is what
   *  stops one loud mechanic (grazing, say) from owning the soundtrack. */
  cooldown: number;
  lines: readonly string[];
};

export type VoiceScript = Record<PoolId, PoolSpec>;

/** Values substituted into `{TOKEN}` placeholders. */
export type Tokens = Record<string, string | number>;

export type VoiceLine = {
  pool: PoolId;
  text: string;
  priority: Priority;
  /** Tick the line started. */
  tick: number;
  /** Tick the utterance is estimated to finish. */
  endsAt: number;
};

/** What the voice needs to know about the world to answer "now?". */
export type VoiceContext = {
  tick: number;
  status: RunStatus;
  /** Player HP as a fraction of max. Routes a hit to `nearDeath`. */
  hpFrac?: number;
};

// ── Tuning ────────────────────────────────────────────────────────────

export type VoiceTuning = {
  /** Estimated ticks of speech per character. 4 ticks/char at 60Hz is
   *  ~15 characters a second, which is roughly conversational TTS. */
  ticksPerChar: number;
  /** Floor and ceiling on an utterance, so a two-word line still gets a
   *  beat of air and a long one can't lock DAEMON out for a phase. */
  minUtterance: number;
  maxUtterance: number;
  /** Silence appended after every line so two lines never touch. */
  trailing: number;
  /** Minimum ticks between the *start* of one line and the start of the
   *  next, by the priority of the incoming line. This is the rate limit
   *  proper: chatter gets a wide gap, critical gets none. */
  gap: Record<Priority, number>;
};

export const VOICE_TUNING: VoiceTuning = {
  ticksPerChar: 4,
  minUtterance: 60,
  maxUtterance: 420,
  trailing: 24,
  // 3 seconds between throwaway lines; 1 second between structural
  // beats; nothing at all in front of something that matters.
  gap: { chatter: 180, beat: 60, critical: 0 },
};

/** Statuses during which the player is reading, not playing. Chatter is
 *  suppressed here; anything `beat` or above still gets through, which
 *  is what lets the draft and results screens have their own lines. */
const READING: ReadonlySet<RunStatus> = new Set<RunStatus>(["draft", "won", "lost"]);

// ── Bag shuffle ───────────────────────────────────────────────────────

export type Bag<T> = {
  /** Next item. Never repeats until the pool is exhausted. */
  draw: () => T;
  /** Items left before the next reshuffle. */
  readonly remaining: number;
};

/**
 * Draw without replacement. A pool of N items yields all N in a
 * seed-stable order before any repeat, and the first draw of a new
 * shuffle is never the last draw of the old one — which is the join
 * where naive bag-shuffles still produce an audible double.
 */
export function createBag<T>(items: readonly T[], rng: Rng): Bag<T> {
  if (items.length === 0) throw new Error("createBag: empty pool");
  let bag: T[] = [];
  let last: T | undefined;

  const refill = (): void => {
    bag = rng.shuffle(items);
    // We draw with pop(), so the *end* of the array is next up.
    if (items.length > 1 && bag[bag.length - 1] === last) {
      const tail = bag.length - 1;
      const swap = bag[0];
      bag[0] = bag[tail];
      bag[tail] = swap;
    }
  };

  return {
    draw(): T {
      if (bag.length === 0) refill();
      const item = bag.pop() as T;
      last = item;
      return item;
    },
    get remaining(): number {
      return bag.length;
    },
  };
}

// ── Formatting ────────────────────────────────────────────────────────

/** Ticks as `m:ss.cc`. The run timer is IGT, so this is exact. */
export function formatTime(ticks: number): string {
  const total = Math.max(0, Math.round(ticks));
  const cs = Math.floor((total % 60) * (100 / 60));
  const secs = Math.floor(total / 60);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
}

/** Substitute `{TOKEN}` placeholders. Returns null if any placeholder in
 *  the line has no value — a line that would leak a literal `{TIME}` to
 *  the screen is worse than no line, so the caller draws another. */
export function fill(text: string, tokens?: Tokens): string | null {
  if (!text.includes("{")) return text;
  let missing = false;
  const out = text.replace(/\{([A-Z][A-Z0-9_]*)\}/g, (whole, key: string) => {
    const v = tokens?.[key];
    if (v === undefined || v === null) {
      missing = true;
      return whole;
    }
    return String(v);
  });
  return missing ? null : out;
}

/** How long a line takes to say, in ticks. Backticks and emphasis marks
 *  are typography, not speech, so they don't count toward the length. */
export function estimateTicks(text: string, tuning: VoiceTuning = VOICE_TUNING): number {
  const spoken = text.replace(/[`*_]/g, "").length;
  const raw = spoken * tuning.ticksPerChar + tuning.trailing;
  return Math.min(tuning.maxUtterance, Math.max(tuning.minUtterance, raw));
}

// ── Event routing ─────────────────────────────────────────────────────

const PHASE_POOL: Record<PhaseId, PoolId> = {
  rl5: "phaseRl5",
  rl4: "phaseRl4",
  rl3: "phaseRl3",
  rl2: "phaseRl2",
  rl1: "phaseRl1",
};

const SIGNAL_POOL: Record<SignalId, PoolId> = {
  sigterm: "sigterm",
  sigstop: "sigstop",
  sigkill: "sigkill",
  renice: "renice",
  strace: "strace",
};

/** HP fraction at or below which a hit becomes a `nearDeath` line. */
export const NEAR_DEATH_HP = 0.3;

export type Routed = { pool: PoolId; tokens?: Tokens };

/**
 * The trigger vocabulary: one sim event in, at most one pool out. Events
 * with no line (`daemon-hit`, `child-down`) return null rather than
 * being handled by omission, so adding a `SimEvent` variant is a type
 * error here rather than a silent hole.
 */
export function routeEvent(ev: SimEvent, ctx: VoiceContext): Routed | null {
  switch (ev.t) {
    case "graze":
      return { pool: "graze" };
    case "player-hit":
      return (ctx.hpFrac ?? 1) <= NEAR_DEATH_HP ? { pool: "nearDeath" } : { pool: "playerHit" };
    case "player-down":
      return { pool: "playerDown" };
    case "daemon-hit":
      // Covered by the signal that caused it. Two lines for one input is
      // exactly the noise this file exists to remove.
      return null;
    case "signal":
      return ev.ok ? { pool: SIGNAL_POOL[ev.id] } : { pool: "signalFail" };
    case "card-start":
      return { pool: "cardStart", tokens: { CARD: ev.name } };
    case "card-clear":
      return { pool: "cardClear", tokens: { CARD: ev.name, TIME: formatTime(ev.ticks) } };
    case "phase-start":
      return { pool: PHASE_POOL[ev.phase], tokens: { NICE: ev.nice } };
    case "phase-clear":
      return { pool: "phaseClear", tokens: { TIME: formatTime(ev.ticks) } };
    case "fork":
      return { pool: "fork", tokens: { COUNT: ev.count } };
    case "child-down":
      return null;
    case "zombie-reaped":
      return { pool: "zombie" };
    case "shrink":
      return { pool: "shrink", tokens: { SCALE: `${Math.round(ev.scale * 100)}%` } };
    case "won":
      return { pool: "won", tokens: { TIME: formatTime(ev.ticks) } };
    case "lost":
      return { pool: "lost", tokens: { TIME: formatTime(ev.ticks) } };
  }
}

// ── The opening ───────────────────────────────────────────────────────

/**
 * The tutorial, such as it is: four cues in the first ten seconds that
 * teach dodging, then grazing, then signals — in that order, because
 * that is the order the mechanics become useful. There is no tutorial
 * screen and there never will be one.
 */
export const OPENING_SCRIPT: readonly { tick: number; pool: PoolId }[] = [
  { tick: 12, pool: "intro" },
  { tick: 120, pool: "teachDodge" },
  { tick: 330, pool: "teachGraze" },
  { tick: 540, pool: "teachSignals" },
];

/** Which opening cue, if any, falls in `(prev, tick]`. */
export function openingCue(prev: number, tick: number): PoolId | null {
  for (const c of OPENING_SCRIPT) if (c.tick > prev && c.tick <= tick) return c.pool;
  return null;
}

// ── The voice ─────────────────────────────────────────────────────────

export type Voice = {
  /**
   * Drain a tick's worth of sim events and speak at most one line. This
   * is the call the shell makes every frame without having to think:
   * priority ordering, deduplication, rate limiting and suppression all
   * happen in here.
   */
  observe: (events: readonly SimEvent[], ctx: VoiceContext) => VoiceLine | null;
  /** Speak from a named pool — for moments the sim doesn't emit, like a
   *  draft pick, a personal best, or a restart. */
  cue: (pool: PoolId, ctx: VoiceContext, tokens?: Tokens) => VoiceLine | null;
  /** The line in flight at `tick`, or null. */
  current: (tick: number) => VoiceLine | null;
  /** True while an utterance is still going. */
  speaking: (tick: number) => boolean;
  /** Forget cooldowns and the current line. Bags are *kept*, so a
   *  restart doesn't re-roll the same opener. */
  reset: () => void;
};

export type VoiceOptions = {
  script: VoiceScript;
  rng: Rng;
  tuning?: Partial<VoiceTuning>;
};

export function createVoice(opts: VoiceOptions): Voice {
  const { script, rng } = opts;
  const tuning: VoiceTuning = {
    ...VOICE_TUNING,
    ...opts.tuning,
    gap: { ...VOICE_TUNING.gap, ...opts.tuning?.gap },
  };

  const bags = new Map<PoolId, Bag<string>>();
  const poolLast = new Map<PoolId, number>();
  let line: VoiceLine | null = null;
  let lastStart = Number.NEGATIVE_INFINITY;

  const bagFor = (pool: PoolId, lines: readonly string[]): Bag<string> => {
    let bag = bags.get(pool);
    if (!bag) {
      bag = createBag(lines, rng);
      bags.set(pool, bag);
    }
    return bag;
  };

  const cue = (pool: PoolId, ctx: VoiceContext, tokens?: Tokens): VoiceLine | null => {
    const spec = script[pool];
    if (!spec || spec.lines.length === 0) return null;
    const { priority } = spec;

    // Never talk over something the player is reading.
    if (priority === "chatter" && READING.has(ctx.status)) return null;

    // Never interrupt an utterance of equal or greater importance.
    if (line && ctx.tick < line.endsAt && RANK[priority] <= RANK[line.priority]) return null;

    // The rate limit: how recently did DAEMON last open his mouth?
    if (ctx.tick - lastStart < tuning.gap[priority]) return null;

    // Category cooldown: the same *kind* of line has to wait its turn.
    const since = ctx.tick - (poolLast.get(pool) ?? Number.NEGATIVE_INFINITY);
    if (since < spec.cooldown) return null;

    // Draw until we get one whose tokens we can actually fill. Bounded
    // by the pool size so an under-supplied caller can't spin.
    const bag = bagFor(pool, spec.lines);
    let text: string | null = null;
    for (let i = 0; i < spec.lines.length && text === null; i++) {
      text = fill(bag.draw(), tokens);
    }
    if (text === null) return null;

    line = {
      pool,
      text,
      priority,
      tick: ctx.tick,
      endsAt: ctx.tick + estimateTicks(text, tuning),
    };
    lastStart = ctx.tick;
    poolLast.set(pool, ctx.tick);
    return line;
  };

  return {
    cue,
    observe(events, ctx) {
      if (events.length === 0) return null;
      const routed: Routed[] = [];
      const seen = new Set<PoolId>();
      for (const ev of events) {
        const r = routeEvent(ev, ctx);
        if (!r || seen.has(r.pool)) continue;
        seen.add(r.pool);
        routed.push(r);
      }
      // Loudest first. `sort` is stable, so ties keep sim order.
      routed.sort((a, b) => {
        const pa = script[a.pool]?.priority ?? "chatter";
        const pb = script[b.pool]?.priority ?? "chatter";
        return RANK[pb] - RANK[pa];
      });
      for (const r of routed) {
        const spoken = cue(r.pool, ctx, r.tokens);
        if (spoken) return spoken;
      }
      return null;
    },
    current(tick) {
      return line && tick < line.endsAt ? line : null;
    },
    speaking(tick) {
      return line !== null && tick < line.endsAt;
    },
    reset() {
      line = null;
      lastStart = Number.NEGATIVE_INFINITY;
      poolLast.clear();
    },
  };
}
